import React, { useMemo } from 'react';

import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryCache } from 'react-query';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { CALENDAR_OPTIONS } from 'src/accounts/AccountList/constants';
import { provisionBotNumber as provisionBotNumberApi } from 'src/accounts/CreateAccount/api';
import {
  LANGUAGE_OPTION,
  TIMEZONE_OPTION,
  EMAIL_REGEX,
  ValidAreaCode,
  HOST,
} from 'src/accounts/constants';
import { IndustryType } from 'src/accounts/types';
import { SelectStyle } from 'src/accounts/utils';
import { Modal, Toast } from 'src/common/components';

import { fetchIndustry, updateAccount } from '../api';
import { STRIPE_BASE_URL, SALESFORCE_BASE_URL, Action } from '../constants';
import { useAccountProfileContext } from '../context';

import SearchCampaignDetails from './SearchCampaignDetails';
import SearchLegalEntities from './SearchLegalEntities';
import SearchParentAccounts from './SearchParentAccounts';
import SearchSalesForceAccounts from './SearchSalesforceAccounts';

type OptionType = { label: string; value: string };
type CallbackType = (industries: IndustryType[]) => void;
const MINUTES_IN_A_DAY = 1440;

type FormType = {
  name: string;
  email: string;
  language: OptionType;
  calendarType: OptionType;
  company_industry: IndustryType;
  timezone: OptionType;
  office_phone: string;
  salesforce_id: string | null;
  appointment_request_window_days: string;
  bot_number_area_code: string;
  parent_account: string | null;
  legal_entity: string | null;
  requests_time_in_advance_minutes: number;
  call_rescue_tests_enabled: boolean;
  campaign_detail: string | null;
};

const Overview: React.FC = () => {
  const { state, dispatch } = useAccountProfileContext();
  const { account } = state;
  const {
    id,
    lang,
    calendar_type,
    timezone,
    bot_numbers: botNumbers,
    features,
    stripe_customer_id,
  } = account;

  const currentParentAccount = account.parent_account;
  const currentLegalEntity = account.legal_entity;
  const currentCampaignDetail = account.campaign_detail;
  const languageOption = useMemo(
    () => LANGUAGE_OPTION.find((language) => language.value === lang),
    [lang]
  );
  const calendarTypeOption = useMemo(
    () => CALENDAR_OPTIONS.find((calendar) => parseInt(calendar.value, 10) === calendar_type),
    [calendar_type]
  );
  const timezoneOption = useMemo(
    () => TIMEZONE_OPTION.find((tz) => tz.value === timezone),
    [timezone]
  );

  const methods = useForm<FormType>({
    defaultValues: {
      ...account,
      parent_account: currentParentAccount?.id,
      legal_entity: currentLegalEntity?.id,
      campaign_detail: currentCampaignDetail?.id,
      language: languageOption,
      calendarType: calendarTypeOption,
      timezone: timezoneOption,
      requests_time_in_advance_minutes: account.requests_time_in_advance_minutes / MINUTES_IN_A_DAY,
    },
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    formState,
    setValue,
  } = methods;
  const { isDirty } = formState;
  const { salesforce_id, parent_account, legal_entity, campaign_detail } = watch();

  const cache = useQueryCache();
  let mainBotNumber;
  let otherBotNumbers;
  if (botNumbers && botNumbers.length > 0) {
    if (botNumbers.length === 1) {
      [mainBotNumber] = botNumbers;
    } else {
      mainBotNumber = botNumbers.find((botNumber) => botNumber.is_call_rescue_number);
      otherBotNumbers = botNumbers
        .map((botNumber) => {
          if (botNumber.is_call_rescue_number) return undefined;
          return botNumber.number;
        })
        .filter(Boolean)
        .join(', ');
    }
  }

  let stripeURL = null;
  if (stripe_customer_id) {
    stripeURL = `${STRIPE_BASE_URL}/${stripe_customer_id}`;
  }
  let salesforceUrl = null;
  if (salesforce_id) {
    salesforceUrl = SALESFORCE_BASE_URL.replace('{salesforce_id}', salesforce_id);
  }
  let parentAccountUrl = null;
  if (parent_account) {
    parentAccountUrl = `${HOST}/bh_staff/cosmos/parentaccount/${parent_account}`;
  }

  let legalEntityUrl = null;
  if (legal_entity) {
    legalEntityUrl = `/app/tcr/legal_entities/${legal_entity}`;
  }

  let campaignDetailUrl = null;
  if (campaign_detail) {
    campaignDetailUrl = `/app/tcr/campaign_details/${campaign_detail}`;
  }

  const [provisionBotNumber, { isLoading }] = useMutation(provisionBotNumberApi, {
    // this will cause a refresh to get the bot number
    onSuccess: () => cache.invalidateQueries('fetchAccount'),
    onError: () => {
      Toast.error('There was an error provisioning bot number.');
    },
  });

  const handleProvisionBotNumber = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    provisionBotNumber({ accountId: account.id || '' });
  };

  const getIndustry = (_: string, callback: CallbackType): void => {
    fetchIndustry()
      .then(({ data }) => {
        callback(data);
      })
      .catch(() => callback([]));
  };

  const onSubmit = async (data: FormType): Promise<void> => {
    const payload = {
      ...data,
      calendar_type: parseInt(data.calendarType.value, 10),
      timezone: data.timezone.value,
      lang: data.language.value,
      company_industry: data.company_industry.id,
      requests_time_in_advance_minutes: data.requests_time_in_advance_minutes * MINUTES_IN_A_DAY,
    };

    // Convert empty string salesforce_id into null for backend
    const { salesforce_id: salesforceId } = payload;

    if (salesforceId === '') {
      payload.salesforce_id = null;
    }

    try {
      await updateAccount(id, payload);
      dispatch({
        type: Action.UPDATE_ACCOUNT_NAME,
        name: data.name,
      });
      Toast.success('Changes saved successfully.');
    } catch (err) {
      Toast.error('There was an error saving changes.');
      reset({
        ...account,
        parent_account: currentParentAccount?.id,
        legal_entity: currentLegalEntity?.id,
        campaign_detail: currentCampaignDetail?.id,
        language: languageOption,
        calendarType: calendarTypeOption,
        timezone: timezoneOption,
      });
    }
  };

  const handleRemoveSalesforceId = (): void => {
    setValue('salesforce_id', null, { shouldDirty: true });
  };

  const handleRemoveParentAccountId = (): void => {
    setValue('parent_account', null, { shouldDirty: true });
  };

  const handleRemoveLegalEntityId = (): void => {
    setValue('legal_entity', null, { shouldDirty: true });
  };

  const handleRemoveCampaignDetailId = (): void => {
    setValue('campaign_detail', null, { shouldDirty: true });
  };

  const validateAreaCode = (value: string): boolean => {
    const areaCodes: string[] = Object.values(ValidAreaCode);
    return areaCodes.includes(String(value));
  };

  return (
    <FormProvider {...methods}>
      <form className="flex flex-col my-6" onSubmit={handleSubmit(onSubmit)} data-testid="overview">
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Account name</h4>
          <div className="flex flex-col">
            <input
              className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
              {...register('name', {
                required: 'Name is required.',
              })}
              data-testid="name"
            />
            {errors.name && <p className="error-text">{errors.name?.message}</p>}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Email</h4>
          <div className="flex flex-col">
            <input
              className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
              {...register('email', {
                required: 'Email is required.',
                pattern: {
                  value: EMAIL_REGEX,
                  message: 'Entered value does not match email format',
                },
              })}
              data-testid="email"
            />
            {errors.email && <p className="error-text">{errors.email?.message}</p>}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Account phone</h4>
          <div className="flex flex-col">
            <input
              className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
              {...register('office_phone', { required: 'Account phone is required.' })}
              data-testid="phone"
            />
            {errors.office_phone && <p className="error-text">{errors.office_phone?.message}</p>}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Language</h4>
          <Controller
            name="language"
            render={({ field }) => (
              <Select
                styles={SelectStyle}
                aria-label="Language"
                placeholder=""
                options={LANGUAGE_OPTION}
                {...field}
              />
            )}
            control={control}
            rules={{ required: true }}
            defaultValue={languageOption}
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4>Bot number</h4>
          {botNumbers.length === 0 ? (
            <Modal
              toggleTarget={
                <button type="button" className="btn btn-secondary">
                  {isLoading ? 'Provisioning bot number' : 'Provision bot number'}
                </button>
              }
              actionFunc={handleProvisionBotNumber}
              cancelText="Cancel"
              actionText="Yes, provision bot number"
            >
              <h3>Are you sure you want to provision bot number?</h3>
            </Modal>
          ) : (
            <>
              {mainBotNumber ? (
                <div className="sm:mt-0 sm:col-span-2 mt-1">
                  <p>
                    <b> Main bot number: </b>
                    {mainBotNumber.number || 'None'};
                  </p>
                  {otherBotNumbers && (
                    <p>
                      <b> Other bot numbers: </b> {otherBotNumbers};
                    </p>
                  )}
                </div>
              ) : (
                <p>
                  Has more than one bot number and we can not determine which one is the main:
                  {otherBotNumbers}
                </p>
              )}
            </>
          )}
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Product calendar type</h4>
          <Controller
            name="calendarType"
            render={({ field }) => (
              <Select
                styles={SelectStyle}
                aria-label="Calendar name"
                {...field}
                placeholder=""
                options={CALENDAR_OPTIONS}
              />
            )}
            control={control}
            rules={{ required: true }}
            defaultValue={calendarTypeOption}
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Industry</h4>
          <Controller
            name="company_industry"
            render={({ field }) => (
              <AsyncSelect
                placeholder=""
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                styles={SelectStyle}
                aria-label="Industry"
                cacheOptions
                defaultOptions
                loadOptions={getIndustry}
                getOptionLabel={(option: IndustryType) => option.name}
                getOptionValue={(option: IndustryType) => {
                  return option.id;
                }}
                {...field}
              />
            )}
            control={control}
            rules={{ required: true }}
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Timezone</h4>
          <Controller
            name="timezone"
            render={({ field }) => (
              <Select
                styles={SelectStyle}
                aria-label="Timezone"
                placeholder=""
                options={TIMEZONE_OPTION}
                {...field}
              />
            )}
            control={control}
            rules={{ required: true }}
            defaultValue={timezoneOption}
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4>Has Active Subscription</h4>
          <p>
            {features.has_active_subscription ? 'Yes ' : 'No '}
            {stripeURL && (
              <a
                className="text-blue-1.0"
                href={stripeURL}
                target="_blank"
                rel="noreferrer"
                data-testid="account-features-stripe-link"
              >
                (View customer details in Stripe)
              </a>
            )}
          </p>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Parent account</h4>
          {parentAccountUrl ? (
            <div className="flex items-center gap-2">
              {currentParentAccount?.name}
              <a
                className="text-blue-1.0 my-auto"
                href={parentAccountUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="parent-account-link"
              >
                (View Parent Account Details)
              </a>
              <button type="button" className="icon-btn ml-2" onClick={handleRemoveParentAccountId}>
                <i className="icon-reject_icon text-lg text-gray-1.0" />
              </button>
            </div>
          ) : (
            <SearchParentAccounts />
          )}
          <input
            id="parent_account"
            {...register('parent_account')}
            defaultValue=""
            data-testid="parent-account-id"
            className="hidden"
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Legal entity</h4>
          {legalEntityUrl ? (
            <div className="flex items-center gap-2">
              {currentLegalEntity?.name}
              <a
                className="text-blue-1.0 my-auto"
                href={legalEntityUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="legal-entity-link"
              >
                (View Legal Entity Details)
              </a>
              <button type="button" className="icon-btn ml-2" onClick={handleRemoveLegalEntityId}>
                <i className="icon-reject_icon text-lg text-gray-1.0" />
              </button>
            </div>
          ) : (
            <SearchLegalEntities />
          )}
          <input
            id="legal_entity"
            {...register('legal_entity')}
            defaultValue=""
            data-testid="legal-entity-id"
            className="hidden"
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Campaign Detail</h4>
          {campaignDetailUrl ? (
            <div className="flex items-center gap-2">
              {currentCampaignDetail?.name}
              <a
                className="text-blue-1.0 my-auto"
                href={campaignDetailUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="legal-entity-link"
              >
                (View Campaign Details)
              </a>
              <button
                type="button"
                className="icon-btn ml-2"
                onClick={handleRemoveCampaignDetailId}
              >
                <i className="icon-reject_icon text-lg text-gray-1.0" />
              </button>
            </div>
          ) : (
            <SearchCampaignDetails />
          )}
          <input
            id="campaign_detail"
            {...register('campaign_detail')}
            defaultValue=""
            data-testid="campaign-detail-id"
            className="hidden"
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Salesforce account</h4>
          {salesforceUrl ? (
            <div className="flex">
              <a
                className="text-blue-1.0 my-auto"
                href={salesforceUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="account-salesforce-link"
              >
                View customer details in SalesForce
              </a>
              <button type="button" className="icon-btn ml-2" onClick={handleRemoveSalesforceId}>
                <i className="icon-reject_icon text-lg text-gray-1.0" />
              </button>
            </div>
          ) : (
            <SearchSalesForceAccounts />
          )}
          <input
            id="salesforce_id"
            {...register('salesforce_id')}
            defaultValue=""
            data-testid="salesforce-id"
            className="hidden"
          />
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Appointment request window days</h4>
          <div className="flex flex-col">
            <input
              className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
              type="number"
              {...register('appointment_request_window_days', {
                required: 'Appointment request window days is required.',
                min: 0,
                valueAsNumber: true,
                pattern: /[0-9]+/,
              })}
              data-testid="appointment-request-window-days"
            />
            {errors?.appointment_request_window_days?.type === 'required' && (
              <p className="error-text">{errors.appointment_request_window_days?.message}</p>
            )}
            {errors?.appointment_request_window_days?.type === 'min' && (
              <p className="error-text">Number must be greater than 0.</p>
            )}
            {errors?.appointment_request_window_days?.type === 'pattern' && (
              <p className="error-text">Value must be a number.</p>
            )}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">
            Requests time in advance days (Days from today required for an appointment request)
          </h4>
          <div className="flex flex-col">
            <input
              className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
              type="number"
              {...register('requests_time_in_advance_minutes', {
                required: 'Requests time in advance days is required.',
                min: 0,
                valueAsNumber: true,
                pattern: /[0-9]+/,
              })}
              data-testid="requests-time-in-advance-minutes"
            />
            {errors?.requests_time_in_advance_minutes?.type === 'required' && (
              <p className="error-text">{errors.requests_time_in_advance_minutes?.message}</p>
            )}
            {errors?.requests_time_in_advance_minutes?.type === 'min' && (
              <p className="error-text">Number must be greater than 0.</p>
            )}
            {errors?.requests_time_in_advance_minutes?.type === 'pattern' && (
              <p className="error-text">Value must be a number.</p>
            )}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <h4 className="my-auto">Bot number area code</h4>
          <div className="flex flex-col">
            <input
              className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
              type="number"
              {...register('bot_number_area_code', {
                required: 'Bot number area code is required.',
                valueAsNumber: true,
                validate: validateAreaCode,
              })}
              data-testid="bot-number-area-code"
            />
            {errors?.bot_number_area_code?.type === 'required' && (
              <p className="error-text">{errors.bot_number_area_code?.message}</p>
            )}
            {errors?.bot_number_area_code?.type === 'validate' && (
              <p className="error-text">
                Value must be a number of three digits and a valid area code.
              </p>
            )}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <div>
            <h4>Call rescue tests enabled</h4>
            <p className="helper-text">Flag to control if account is opt-in call rescue tests.</p>
          </div>
          <input
            type="checkbox"
            className="w-5 h-5"
            data-testid="call_rescue_tests_enabled"
            {...register('call_rescue_tests_enabled')}
          />
        </div>
        {isDirty && (
          <p className="helper-text mt-2 ml-auto">**Friendly reminder to save changes.</p>
        )}
        <button className="btn btn-secondary mt-4 ml-auto" type="submit" data-testid="submit">
          Save changes
        </button>
      </form>
    </FormProvider>
  );
};

export default Overview;
